/**
 * Sample ledger facts and disagreements.
 *
 * Three cases, because the module's value is telling them apart:
 *   a systematic difference worth acting on,
 *   noise that looks like one until it is measured,
 *   and a pair with nothing in common, which is not agreement.
 */
import type { Divergence, FactSummary } from "../domain/ledger";
import type { DivergenceQuery } from "../api/dataSource";

export function mockLedgerFacts(): FactSummary[] {
  return [
    {
      factKey: "ready_at",
      assertions: 36,
      parties: 2,
      partyKeys: ["airline", "handler"],
      subjects: 18,
      comparableSubjects: 18,
      latest: new Date(Date.now() - 3600_000).toISOString(),
      /* nobody has been nominated to settle it — a contract gap, not a data gap */
      authority: undefined,
    },
    {
      factKey: "chocks_on",
      assertions: 44,
      parties: 2,
      partyKeys: ["airline", "handler"],
      subjects: 22,
      comparableSubjects: 22,
      latest: new Date(Date.now() - 7200_000).toISOString(),
      authority: "handler",
    },
    {
      factKey: "bags_delivered",
      assertions: 15,
      parties: 2,
      partyKeys: ["airline", "handler"],
      subjects: 15,
      /* both parties report, but never on the same subject */
      comparableSubjects: 0,
      latest: new Date(Date.now() - 86400_000).toISOString(),
      authority: undefined,
    },
  ];
}

export function mockDivergence(q: DivergenceQuery): Divergence {
  const base = { fact: q.factKey, partyA: q.partyA, partyB: q.partyB };

  if (q.factKey === "bags_delivered") {
    return {
      ...base,
      comparable: false,
      whyNot:
        "these two parties have never reported on the same subject. That is not agreement — there is " +
        "nothing here to agree or disagree about.",
      pairs: 0,
      systematic: false,
    };
  }

  if (q.factKey === "chocks_on") {
    return {
      ...base,
      comparable: true,
      pairs: 22,
      medianSeconds: 40,
      medianMinutes: 0.7,
      iqrSeconds: 260,
      bLaterPct: 45.5,
      systematic: false,
      finding:
        "The two parties differ by a median of 0.7 minutes across 22 paired observations, and the " +
        "difference changes direction about as often as not. That is two parties measuring a fuzzy " +
        "event slightly differently, and there is nothing to act on.",
    };
  }

  return {
    ...base,
    comparable: true,
    pairs: 18,
    medianSeconds: -930,
    medianMinutes: -15.5,
    iqrSeconds: 195,
    bLaterPct: 0,
    systematic: true,
    finding:
      `${q.partyA} is consistently 15.5 minutes later than ${q.partyB} on 'ready_at', across 18 paired ` +
      "observations, 18 of them in the same direction. That is a process difference, not measurement noise.",
  };
}
